import { useSelector } from 'react-redux';

import { useQuery } from 'hooks/useCustomQuery';

import getDashboardCompanyServices from 'API/Dashboard/getDashboardCompanyServices';

/**
 * Fetches consolidated dashboard payload (services + app auths) for a company.
 *
 * Response shape (backend):
 * { companyID, companyApproved, services: [], appAuths: [] }
 */
function useDashboardCompanyServices({
    currentCompany,
    flow,
    queryKey = ['dashboardServices', flow, `company: ${currentCompany}`],
    options = {},
}) {
    const currentCompanyData = useSelector(
        (state) => state.navigationLists.currentCompanyData,
    );
    return useQuery({
        queryKey,
        queryFn: async () => {
            const res = await getDashboardCompanyServices(currentCompany);
            if (res.status !== 200) {
                throw new Error(
                    res.message || 'Error fetching dashboard services payload',
                );
            }

            const payload = res.data || {};

            // Normalize service payload to match current dashboard expectations
            const services = (payload.services || []).map((service) => {
                const normalized =
                    service.appAuthType === 'Webex'
                        ? { ...service, appAuthType: 'WebexAppAuth' }
                        : service;
                // Rehydrate company-level fields the flat payload omits so the
                // service shape matches what Grant/Request Access expect.
                return {
                    ...normalized,
                    companyID: normalized.companyID ?? payload.companyID,
                    company: normalized.company ?? {
                        selectiveSyncPolicy:
                            currentCompanyData?.selectiveSyncPolicy,
                    },
                };
            });

            return {
                ...payload,
                services,
            };
        },
        ...options,
    });
}

export default useDashboardCompanyServices;
